import React, { useState, useEffect, useRef } from "react";
import {
    Box, Button, TextField, Typography, Grid, FormControl, Checkbox,
    FormControlLabel,
    Switch
} from "@mui/material";
import { ShippingDocumentViewModel, ShippingDocumentLineViewModel, Option, SalesDocumentAction, SalesDocumentActionResult, PrintAction } from "../../../Types/ExportTypes";
import ToolbarWithSave, { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../../Context/Contexts";


import AsyncAutoComplete from "../../../components/AsyncAutoComplete";
import ShippingAgentServiceSelect from "../../../components/Shipping/ShippingAgentServiceSelect";
import {
    CountryAutoComplete, CustomerAutoComplete
} from "../../../components/AutoComplete/AutoCompletes";

import SalesDocumentActions from "../../../components/SalesDocument/SalesDocumentActions";
import ShipmentDocumentLines from "../../../components/Shipping/ShipmentDocumentLines";
import ShipmentParcelLines from "../../../components/Shipping/ShipmentParcelLines";
import { useTranslation } from "react-i18next";

const ShipmentDocumentEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { documentNo = "" } = params || {};
    const { t } = useTranslation();
    const { authService } = useAuth();
    const { goBack, navigate } = useNavigation();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();
    const [loadedOptions, setloadedOptions] = useState(false);

    const [viewModel, setViewModel] = useState<ShippingDocumentViewModel | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [shippingAgents, setShippingAgents] = useState<Option[]>([]);
    const [shippingServices, setShippingServices] = useState<Option[]>([]);

    const [showShippingInfo, setShowShippingInfo] = useState(false);
    const [showParcels, setShowParcels] = useState(false);

    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

    const hasPendingChanges = () => viewModel?.hasChanges === true;

    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };

    const preventIfUnsaved = () => {
        if (hasPendingChanges()) {
            showHasChangesError();
            return true;
        }
        return false;
    };

    //const [snackbar, setSnackbar] = useState<Pick<AlertProps, "children" | "severity"> | null>(null);

    //const handleCloseSnackbar = () => setSnackbar(null);




    const fetchDocument = async (no: string) => {
        setIsLoading(true);
        try {
            const response = await authService.get<ShippingDocumentViewModel>(`/shipmentDocumentApi/GetDocument?documentNo=${documentNo}`);
            setViewModel(
                response
                    ? { ...response, originalValues: { ...response }, hasChanges: false }
                    : null
            );
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);


    const fetchShippingAgentOptions = async (
        search: string,
        offset: number,
        code?: string | null
    ): Promise<Option[]> => {
        const url = code
            ? `/autocompleteApi/ShippingAgent?code=${code}`
            : `/autocompleteApi/ShippingAgent?search=${search}&offset=${offset}&limit=50`;

        const response = await authService.get<Option[]>(url);
        return response || [];
    };

    const fetchShippingServiceOptions = async (
        search: string,
        offset: number,
        code?: string | null,
        shippingAgentCode?: string
    ): Promise<Option[]> => {
        if (!viewModel?.shippingAgentCode && !shippingAgentCode) return [];
        if (code) {
            const response = await authService.get<Option[]>(
                `/autocompleteApi/ShippingService?shippingAgentCode=${shippingAgentCode ?? viewModel?.shippingAgentCode}&code=${code}`
            );
            return response || [];
        }



        const response = await authService.get<Option[]>(
            `/autocompleteApi/ShippingService?shippingAgentCode=${shippingAgentCode ?? viewModel?.shippingAgentCode}&search=${search}&offset=${offset}&limit=50`
        );
        return response || [];
    };




    const initializeDropdowns = async () => {
        try {
            setIsLoading(true);
            const [shippingAgentList, shippingServiceList] = await Promise.all([

                fetchShippingAgentOptions("", 0, viewModel?.shippingAgentCode),
                fetchShippingServiceOptions("", 0, viewModel?.shippingServiceCode)
            ]);


            setShippingAgents(shippingAgentList || []);
            setShippingServices(shippingServiceList || []);
        } catch (error) {
            console.error("Failed to initialize dropdown options:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (documentNo) fetchDocument(documentNo);

    }, [documentNo]);

    useEffect(() => {
        if (loadedOptions === false && (documentNo === "" || documentNo !== "" && viewModel?.no === documentNo)) {
            initializeDropdowns();
            setloadedOptions(true);
        }
    }, [viewModel, documentNo]);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    const handleSave = async () => {
        setIsLoading(true);
        try {
            var response = await authService.post<ShippingDocumentViewModel>("/shipmentDocumentApi/UpdateShipmentHeader", viewModel);
            setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
            showSnackbar(t("dialog.savedSuccessfully"), "success");
        } catch (error: any) {
            showSnackbar(`${t("dialog.saveErrorGeneric")} :  ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = async (key: keyof ShippingDocumentViewModel, value: any) => {
        let newDocument: ShippingDocumentViewModel | null = null;

        setViewModel((prev) => {
            if (!prev) return null;

            newDocument = { ...prev, [key]: value, hasChanges: true };
            return newDocument;
        });
    };

    const handleChanges = (changes: Partial<ShippingDocumentViewModel>) => {
        setViewModel((prev) => {
            if (!prev) return null;
            return { ...prev, ...changes, hasChanges: true };
        });
    };

    const handleCancelChanges = () => {
      //  setIsLoading(true);
        try {
            setViewModel((prev) => {
                if (!prev || !prev.originalValues) return prev;
                const original = prev.originalValues;
                const changes: Partial<ShippingDocumentViewModel> = {};
                for (const key in original) {
                    const typedKey = key as keyof ShippingDocumentViewModel;
                    const originalValue = original[typedKey];
                    const currentValue = prev[typedKey];
                    if (originalValue !== currentValue) {
                        (changes as any)[typedKey] = originalValue;
                    }
                }
                 if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };
                return { ...prev, ...changes, hasChanges: false };
            });
        } finally {
         //   setIsLoading(false);
        }
    };






    const onPrint = async () => {
        if (preventIfUnsaved()) return;
        setIsLoading(true);
        try {
            console.log("Printing...");
            const body: PrintAction = {
                documentNo: viewModel?.no ?? "",
            };
            const response = await authService.post<Blob>("/printApi/PrintShipmentDocument", body, "blob");

            showSnackbar(t("dialog.downloadSuccess"), "success");
            // Convert response Blob to object URL
            const url = window.URL.createObjectURL(response);
            window.open(url, "_blank");

            // Optional: Cleanup memory
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        } catch (error: any) {
            console.error("Error during Print:", error);
            showSnackbar(`${t("common.failedToPrint")} : ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            setIsLoading(false);
        }
    };



    const onIntegrate = async () => {
        if (preventIfUnsaved()) return;
        setIsLoading(true);
        try {
            const body: SalesDocumentAction = {
                documentNo: viewModel?.no ?? "",
                action: "Integrate"
            };
            const response = await authService.post<SalesDocumentActionResult>("/shipmentDocumentApi/DocumentAction", body);
            showSnackbar(`Integrated ${documentNo}`, "success");
        } catch (error: any) {
            console.error("Error during integrate:", error);
            showSnackbar(`${t("dialog.saveErrorGeneric")} :  ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            setIsLoading(false);
        }
    };

    const onIntegrateTa = async () => {
        if (preventIfUnsaved()) return;
        setIsLoading(true);
        try {
            const body: SalesDocumentAction = {
                documentNo: viewModel?.no ?? "",
                action: "integrateTa"
            };
            const response = await authService.post<SalesDocumentActionResult>("/shipmentDocumentApi/DocumentAction", body);
            showSnackbar(`Integrated TA for ${documentNo}`, "success");
            setIsLoading(false);
            navigate({ path: "/sales/shipment/edit", params: { documentNo } });
        } catch (error: any) {
            console.error("Error during SendEmail:", error);
            showSnackbar(`${t("dialog.saveErrorGeneric")} :  ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            setIsLoading(false);
        }
    };

    const onSendEmail = async () => {
        if (preventIfUnsaved()) return;
        setIsLoading(true);
        try {
            console.log("Sending Email...");
            const body: SalesDocumentAction = {
                documentNo: viewModel?.no ?? "",
                action: "SendEmail"
            };
            const response = await authService.post<SalesDocumentActionResult>("/shipmentDocumentApi/DocumentAction", body);
            console.log("SendEmail completed:", response);

            if (response?.result) {
                alert("Email sent successfully!");
            }
        } catch (error: any) {
            console.error("Error during SendEmail:", error);
            showSnackbar(`${t("dialog.saveErrorGeneric")} :  ${error.response?.data ?? error.message}`, "error");
        }
        finally {
            setIsLoading(false);
        }
    };



    if (!viewModel) {
        return <Typography>{t("common.loadingdotdot")}</Typography>;
    }


    return (
        <Box sx={{ padding: "16px", position: "relative", minHeight: "100vh" }}>
            <ToolbarWithSave
                ref={toolbarRef}
                onSave={handleSave}
                onCancel={handleCancelChanges}
                isSaving={isLoading}
                hasChanges={viewModel?.hasChanges ?? false}
            />

            <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                padding: "16px",
                backgroundColor: "background.default"
            }}>
                <Box sx={{
                    maxWidth: "900px",
                    width: "100%",
                    backgroundColor: "background.paper",
                    padding: 3,
                    borderRadius: 2,
                    boxShadow: 3
                }}>


                    <Typography variant="h4" sx={{
                        mb: 2, textAlign: "center"
                    }} >
                        {"Shipment"}

                    </Typography>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        maxWidth: "fit-content",
                        mb: 2
                    }}>
                        <SalesDocumentActions
                            onIntegrate={onIntegrate}
                            onReintegrateTa={onIntegrateTa}
                            onPrint={onPrint}
                            onSendEmail={onSendEmail} />

                    </Box>
                    {isLoading && <Typography> {t("common.loadingdotdot")}</Typography>}
                    {/* Button to open SalesDocumentLines modal */}

                    <Grid container spacing={2} >
                        <Grid item xs={12} md={12}>
                            <TextField
                                label={t("common.shipmentNo")}
                                value={viewModel.no}
                                fullWidth
                                disabled
                                margin="normal"
                            />
                            <TextField
                                label={t("common.orderNo")}
                                value={viewModel.orderNo}
                                fullWidth
                                disabled
                                margin="normal"
                            />
                            <FormControl fullWidth margin="normal">
                                <CustomerAutoComplete
                                    key={viewModel.selltoCustomerNo || null}
                                    valueId={viewModel.selltoCustomerNo || null}
                                    onChange={(selected) => { }}
                                    label={t("common.shipToCustomer")}
                                    disabled
                                    infoText={t("help.shipToCustomer")}
                                />

                            </FormControl>



                        </Grid>


                        {/* Switch for Shipping Information */}

                        <Grid item xs={12} >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showShippingInfo}
                                        onChange={(e) => setShowShippingInfo(e.target.checked)}
                                    />
                                }
                                label={t("common.showShippingInfo")}
                            />
                        </Grid>

                        {/* Shipping Information Section */}
                        {showShippingInfo && (
                            <>
                                <Box
                                    sx={{
                                        backgroundColor: "secondary.main",
                                        padding: 2,
                                        borderRadius: 2,
                                    }}
                                >
                                    <Grid item xs={12} >
                                        <TextField
                                            label={t("common.shipToName")}
                                            value={viewModel.shiptoName}
                                            onChange={(e) => handleChange("shiptoName", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />
                                        <TextField
                                            label={t("common.shipToName2")}
                                            value={viewModel.shiptoName2}
                                            onChange={(e) => handleChange("shiptoName2", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />
                                        <TextField
                                            label={t("common.shipToAddress")}
                                            value={viewModel.shiptoAddress}
                                            onChange={(e) => handleChange("shiptoAddress", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />

                                        <TextField
                                            label={t("common.shipToAddress2")}
                                            value={viewModel.shiptoAddress2}
                                            onChange={(e) => handleChange("shiptoAddress2", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />

                                        <TextField
                                            label={t("common.shipToMobile")}
                                            value={viewModel.shipToMobileNo}
                                            onChange={(e) => handleChange("shipToMobileNo", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />
                                        <TextField
                                            label={t("common.shipToEmail")}
                                            value={viewModel.shipToEmail}
                                            onChange={(e) => handleChange("shipToEmail", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />
                                    </Grid>
                                    <Grid item xs={12} >
                                        <TextField
                                            label={t("common.shipToCounty")}
                                            value={viewModel.shiptoCounty}
                                            onChange={(e) => handleChange("shiptoCounty", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />
                                        <TextField
                                            label={t("common.shipToPostCode")}
                                            value={viewModel.shiptoPostCode}
                                            onChange={(e) => handleChange("shiptoPostCode", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />
                                        <TextField
                                            label={t("common.shipToCity")}
                                            value={viewModel.shiptoCity}
                                            onChange={(e) => handleChange("shiptoCity", e.target.value)}
                                            fullWidth
                                            margin="normal"

                                        />
                                        <FormControl fullWidth margin="normal">
                                            <CountryAutoComplete
                                                key={viewModel.shiptoCountryCode || null}
                                                valueId={viewModel.shiptoCountryCode || null}
                                                onChange={(selected) => handleChange("shiptoCountryCode", selected?.value || "")}
                                                label={t("common.shipToCountry")}

                                            />

                                        </FormControl>


                                    </Grid>
                                </Box>
                            </>
                        )}


                        <Grid item xs={12} >
                            <FormControl fullWidth margin="normal">
                                <ShippingAgentServiceSelect
                                    shippingAgentCode={viewModel.shippingAgentCode}
                                    shippingServiceCode={viewModel.shippingServiceCode}
                                    onChange={(agent, service) =>
                                        handleChanges({ shippingAgentCode: agent, shippingServiceCode: service })
                                    }
                                    infoTextAgent={t("help.shippingAgent")}
                                    infoTextService={t("help.shippingService")}
                                    isLoading={isLoading}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showParcels}
                                        onChange={(e) => setShowParcels(e.target.checked)}
                                    />
                                }
                                label={t("common.showParcels")}
                            />
                        </Grid>

                        {/* Shipping Information Section */}
                        {showParcels && (
                            <>
                                <Box sx={{
                                    padding: "16px", width: "98vw",
                                    borderRadius: 2,
                                    backgroundColor: "secondary.main"
                                }}>
                                    <Grid item xs={12}>



                                        <ShipmentParcelLines
                                            documentNo={documentNo}

                                        />


                                    </Grid>
                                </Box>
                            </>
                        )}

                        <Box sx={{
                            padding: "16px", width: "98vw",
                            borderRadius: 2
                        }}>
                            <Grid item xs={12}>



                                <ShipmentDocumentLines
                                    documentNo={documentNo}

                                />


                            </Grid>
                        </Box>
                    </Grid>


                    <Box sx={{ marginTop: 2, textAlign: "center" }}>

                        {/* Button to open Additional Fields modal */}
                        {/*<Button variant="outlined" onClick={toggleFieldsModal} sx={{ marginBottom: 2 }}>*/}
                        {/*    Open Additional Fields*/}
                        {/*</Button>*/}
                        <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
                            {t("common.save")}
                        </Button>
                        <Button variant="outlined" onClick={goBack}>
                            {t("common.back")}
                        </Button>
                    </Box>

                    {/* Modal for SalesDocumentLines */}
                    {/*<Modal open={modalOpenLines} onClose={toggleLinesModal}>*/}
                    {/*    <Box*/}
                    {/*        sx={{*/}
                    {/*            padding: 4,*/}
                    {/*            backgroundColor: "white",*/}
                    {/*            borderRadius: "8px",*/}
                    {/*            maxWidth: "90%",*/}
                    {/*            width: "800px", // Fixed width*/}
                    {/*            maxHeight: "90vh",*/}
                    {/*            overflow: "auto",*/}
                    {/*            margin: "auto",*/}
                    {/*            mt: 4,*/}
                    {/*        }}*/}
                    {/*    >*/}
                    {/*        <Typography variant="h6" gutterBottom>*/}
                    {/*            Sales Document Lines*/}
                    {/*        </Typography>*/}
                    {/*        */}{/*<SalesDocumentLines*/}
                    {/*        */}{/*    documentNo={salesDocument?.no}*/}
                    {/*        */}{/*    documentType={1}*/}
                    {/*        */}{/*    readUrl={"/orderApi/GetOrderLines"}*/}
                    {/*        */}{/*    updateUrl={"taco"}*/}
                    {/*        */}{/*    deleteUrl={"taco"}*/}
                    {/*        */}{/*/>*/}
                    {/*        <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>*/}
                    {/*            <Button variant="contained" onClick={toggleLinesModal}>*/}
                    {/*                Close*/}
                    {/*            </Button>*/}
                    {/*        </Box>*/}
                    {/*    </Box>*/}
                    {/*</Modal>*/}


                </Box>
            </Box>

        </Box>
    );
};

export default ShipmentDocumentEditPage;
